/**
 * Date Range Strategy
 *
 * Handles date range selection logic for search functionality.
 * Implements a simple two-step selection: start date, then end date.
 *
 * Following SOLID principles:
 * - Single Responsibility: Only handles date range selection logic
 * - Open/Closed: Can be extended for booking without modification
 */

class DateRangeStrategy {
  constructor(maxRangeDays = 90) {
    this.maxRangeDays = maxRangeDays;
  }

  /**
   * Process a date selection
   * @param {Date} date - The date being selected
   * @param {Date[]} currentSelection - Currently selected dates
   * @returns {Date[]} New selection array
   */
  selectDate(date, currentSelection) {
    // No selection yet - set as start date
    if (currentSelection.length === 0) {
      return [date];
    }

    // Have start date - complete the range
    if (currentSelection.length === 1) {
      const start = currentSelection[0];

      // If clicked date is before start, reset and start over
      if (date < start) {
        return [date];
      }

      // Complete the range
      return [start, date];
    }

    // Have complete range - start new selection
    return [date];
  }

  /**
   * Check if selection is complete (has both start and end dates)
   * @param {Date[]} selection - Current selection
   * @returns {boolean}
   */
  isSelectionComplete(selection) {
    return selection.length === 2;
  }

  /**
   * Validate the selection
   * @param {Date[]} selection - Dates to validate
   * @returns {Object} Validation result
   */
  validate(selection) {
    if (selection.length === 0) {
      return { isValid: false, error: 'Please select a start date' };
    }

    if (selection.length === 1) {
      return { isValid: false, error: 'Please select an end date' };
    }

    const [start, end] = selection;

    if (end <= start) {
      return { isValid: false, error: 'End date must be after start date' };
    }

    // Check if range exceeds maximum
    const daysDiff = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    if (daysDiff > this.maxRangeDays) {
      return {
        isValid: false,
        error: `Date range cannot exceed ${this.maxRangeDays} days`
      };
    }

    return { isValid: true };
  }

  /**
   * Get human-readable display of selection
   * @param {Date[]} selection - Selected dates
   * @returns {string} Formatted display string
   */
  getSelectionDisplay(selection) {
    if (selection.length === 0) {
      return 'Select start date';
    }

    if (selection.length === 1) {
      return `${this.formatDate(selection[0])} - Select end date`;
    }

    return `${this.formatDate(selection[0])} - ${this.formatDate(selection[1])}`;
  }

  /**
   * Get the selectable date range based on current selection
   * @param {Date[]} selection - Current selection
   * @returns {Object|null} Range constraints
   */
  getSelectableRange(selection) {
    if (selection.length === 1) {
      // After selecting start date, limit end date
      const maxDate = new Date(selection[0]);
      maxDate.setDate(maxDate.getDate() + this.maxRangeDays);

      return {
        minDate: selection[0],
        maxDate: maxDate
      };
    }

    return null;
  }

  /**
   * Check if a date is within the selected range (for styling)
   * @param {Date} date - Date to check
   * @param {Date[]} selection - Current selection
   * @returns {boolean}
   */
  isDateInRange(date, selection) {
    if (selection.length < 2) {
      return false;
    }

    const [start, end] = selection;
    return date > start && date < end;
  }

  /**
   * Format a date for display
   * @private
   * @param {Date} date - Date to format
   * @returns {string} Formatted date string
   */
  formatDate(date) {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  }

  /**
   * Clear selection state
   */
  clearSelection() {
    // No internal state to clear for search use case
  }
}

export default DateRangeStrategy;
